// ---------------------------------------------------------------------------
// Label printing transports.
//
// A browser cannot write to a USB printer, so something local has to do it. Two
// options are supported and both are probed; whichever answers is used:
//
//   bridge  — the Zebra Print Bridge agent (installed from ZebraPrintBridge.msi),
//             listening on 127.0.0.1:9110
//   browser — Zebra Browser Print, if the site has its SDK loaded
//
// Unlike the rest of services/, these talk to the operator's own machine rather
// than the API, so there is no token and no REACT_APP_API_URL.
// ---------------------------------------------------------------------------

const BRIDGE_URL = process.env.REACT_APP_PRINT_BRIDGE_URL || "http://127.0.0.1:9110";

export const SETUP_HINT =
  "No printer connection. Install Zebra Print Bridge on this machine and start it " +
  "(`zebra-bridge start`), or install Zebra Browser Print.";

const bridgeCall = async (path, init) => {
  const res = await fetch(BRIDGE_URL + path, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Print bridge returned ${res.status}`);
  return body;
};

const browserAvailable = () => typeof window !== "undefined" && Boolean(window.BrowserPrint);

/** Which transports are reachable right now. */
export async function probeTransports() {
  let bridge = false;
  try {
    const health = await bridgeCall("/health");
    bridge = Boolean(health.ok);
  } catch (e) {
    bridge = false;
  }
  return { bridge, browser: browserAvailable() };
}

const browserDevices = () =>
  new Promise((resolve) => {
    if (!browserAvailable()) return resolve([]);
    window.BrowserPrint.getLocalDevices(
      (list) => resolve(Array.isArray(list) ? list : (list && list.printer) || []),
      () => resolve([]),
      "printer"
    );
  });

/** Every printer either transport can see, tagged with where it came from. */
export async function discoverPrinters() {
  const results = await Promise.all([
    bridgeCall("/printers")
      .then((r) => (r.printers || []).map((p) => ({ ...p, transport: "bridge" })))
      .catch(() => []),
    browserDevices().then((list) =>
      list.map((d) => ({
        uid: d.uid,
        name: d.name || d.uid,
        serial: null,
        transport: "browser",
        device: d,
      }))
    ),
  ]);
  return results[0].concat(results[1]);
}

/** Send raw ZPL to one printer. */
export function sendZpl(printer, zpl) {
  if (!printer) return Promise.reject(new Error("No printer selected."));

  if (printer.transport === "browser") {
    return new Promise((resolve, reject) => {
      printer.device.send(zpl, resolve, (e) => reject(new Error(String(e))));
    });
  }

  return bridgeCall("/print", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uid: printer.uid, zpl }),
  });
}

/** Fallback when no transport is available: hand the operator the .zpl file. */
export function downloadZpl(zpl, filename) {
  const url = URL.createObjectURL(new Blob([zpl], { type: "text/plain" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "label.zpl";
  a.click();
  URL.revokeObjectURL(url);
}
